import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import ReactFocusLock from 'react-focus-lock';

import {
	MapsFiguresOverviewFieldsFragment,
	PermitsOverviewFieldsFragment,
	SubsidyOverviewFieldsFragment,
	OpenGelderlandWooDocumentOverviewFragment,
	ComponentModulesOpenWooRequestOverview
} from 'api/graphql-types';
import { IActiveFilter } from 'redux/stores/subsidy';
import { Button } from 'components/button';
import { CheckboxGroup } from 'components/checkbox-group/checkbox-group';
import { Icon } from 'components/icon/icon';
import { RadioButtonGroup } from 'components/radio-button-group/radio-button-group';
import { RadioButtonItem } from 'components/core/radio-button-group';
import { CheckboxItem } from 'components/core/checkbox-group';

import CrossIcon from 'public/assets/icons/cross.svg';
import IconChevronLeft from 'public/assets/icons/chevron-left.svg';
import PlusIcon from 'public/assets/icons/plus.svg';

import { FilterGroupTitle } from './filter-group-title';
import styles from './filter-sidebar.module.scss';

export interface IFilterSidebarProps {
	module:
		| SubsidyOverviewFieldsFragment
		| PermitsOverviewFieldsFragment
		| MapsFiguresOverviewFieldsFragment
		| OpenGelderlandWooDocumentOverviewFragment
		| ComponentModulesOpenWooRequestOverview;
	mobileIsOpen: boolean;
	setMobileIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	filters: IFilters;
	amountOfActiveFilters: number;
	activeFilters: IActiveFilter[];
	handleDeleteFilters: (item: any) => void;
	handleResetFilter: (activeFilter: IActiveFilter) => void;
	handleShowMore?: () => void;
}

export interface IBaseFilter {
	id: string;
	title: string;
	showMore?: string;
	showLess?: string;
	maxItemsAmount?: number;
}

export interface IRadioFilter extends IBaseFilter {
	type: 'radio';
	value?: RadioButtonItem;
	options: RadioButtonItem[];
	onChange: (item: RadioButtonItem, active?: boolean) => void;
}

export interface ICheckboxFilter extends IBaseFilter {
	type: 'checkbox';
	value: CheckboxItem[];
	options: CheckboxItem[];
	onChange: (item: CheckboxItem[], active?: boolean) => void;
	handleShowMore?: () => void;
}

export type IFilter = IRadioFilter | ICheckboxFilter;

export type IFilters = IFilter[];

export const FilterSidebar = ({
	module,
	mobileIsOpen,
	setMobileIsOpen,
	filters,
	amountOfActiveFilters,
	activeFilters,
	handleDeleteFilters,
	handleResetFilter
}: IFilterSidebarProps): React.JSX.Element => {
	const intl = useIntl();
	const [currentFilter, setCurrentFilter] = useState<{ identifier: string; title: string } | undefined>();
	const hasFiltersSelected = amountOfActiveFilters > 0;

	const onEnterTitle = (event: React.KeyboardEvent<HTMLElement>, identifier: string, title: string): void => {
		if (event.key === 'Enter') {
			setCurrentFilter({ identifier, title });
		}
	};

	useEffect(() => {
		if (!mobileIsOpen) {
			setCurrentFilter(undefined);
		}
	}, [mobileIsOpen]);

	return (
		<ReactFocusLock
			disabled={!mobileIsOpen}
			autoFocus={mobileIsOpen}
			className={clsx(styles.filter, mobileIsOpen && styles.filterOpen, currentFilter && styles.subFilterOpen)}
		>
			<div className={styles.filterHeader}>
				<h3 id={currentFilter ? currentFilter.title : module.filterHeaderText}>
					{currentFilter && (
						<button type="button" onClick={() => setCurrentFilter(undefined)}>
							<Icon customIcon={<IconChevronLeft />} size="14px" />
						</button>
					)}
					{currentFilter ? currentFilter.title : module.filterHeaderText}
					<button
						type="button"
						onClick={() => setMobileIsOpen(false)}
						aria-label={intl.formatMessage({ id: 'filterButton.close' })}
					>
						<Icon customIcon={<CrossIcon />} size="14px" />
					</button>
				</h3>

				{hasFiltersSelected ? (
					<ul className={styles.filterList}>
						{activeFilters.map((filter, i) => (
							<li key={`activeFilters-${i}-${filter.id}`} className={styles.filterListItem}>
								<button
									type="button"
									onClick={() => handleResetFilter(filter)}
									aria-controls="pageResults"
									aria-label={intl.formatMessage({ id: 'filterButton.deleteFilter' }, { filter: filter.label })}
								>
									<Icon customIcon={<CrossIcon />} size="12px" color="black" />
									<span>{filter.label}</span>
								</button>
							</li>
						))}
					</ul>
				) : (
					<div className={styles.noFiltersSelected}>{intl.formatMessage({ id: 'global.filter.noneSelected' })}</div>
				)}
				<div className={styles.deleteFiltersButton}>
					{hasFiltersSelected && (
						<Button
							variant="tertiary-dark"
							title={intl.formatMessage(
								{ id: 'filterButton.deleteFilters' },
								{
									amount: amountOfActiveFilters
								}
							)}
							onClick={handleDeleteFilters}
							aria-controls="pageResults"
						/>
					)}
				</div>
			</div>

			<div className={clsx(styles.filterBody, hasFiltersSelected && styles.filterBodySmall)}>
				{filters.map((item) => (
					<fieldset
						key={`filter-group-${item.id}`}
						className={clsx(
							styles.filterGroup,
							currentFilter && currentFilter.identifier === item.id && styles.filterGroupActive
						)}
					>
						<legend className={styles.filterGroupTitle}>
							<FilterGroupTitle
								title={item.title}
								identifier={item.id}
								onEnter={onEnterTitle}
								setCurrentFilter={setCurrentFilter}
							/>
						</legend>
						<div>
							{item.type === 'checkbox' &&
								(item.handleShowMore && !mobileIsOpen ? (
									<>
										<CheckboxGroup
											groupName={item.id}
											items={
												item.options.length > (item.maxItemsAmount || 4)
													? item.options.splice(0, item.maxItemsAmount || 4)
													: item.options
											}
											onChange={item.onChange}
											selectedItems={item.value || activeFilters.find((activeItem) => activeItem.id === item.id)}
											singleColumn
											sortByName={item.options.length > 0}
											aria-controls="pageResults"
										/>
										<button type="button" className={styles.showAll} onClick={item?.handleShowMore}>
											<Icon customIcon={<PlusIcon />} size="16px" />
											<span>{item.showMore || intl.formatMessage({ id: 'global.showAll' })}</span>
										</button>
									</>
								) : (
									<CheckboxGroup
										groupName={item.id}
										items={item.options}
										onChange={item.onChange}
										selectedItems={item.value || activeFilters.find((activeItem) => activeItem.id === item.id)}
										singleColumn
										sortByName={(item.options || []).length > 0}
										showMoreIcon={<Icon customIcon={<PlusIcon size="16px" />} size="16px" />}
										showLessLabel={item.showLess || intl.formatMessage({ id: 'global.showLess' })}
										showMoreLabel={item.showMore || intl.formatMessage({ id: 'global.showAll' })}
										maxShown={item.maxItemsAmount || 4}
										aria-controls="pageResults"
									/>
								))}

							{item.type === 'radio' && (
								<RadioButtonGroup
									id={item.id}
									items={item.options}
									onChange={item.onChange}
									selectedItem={item.value}
									singleColumn
									aria-controls="pageResults"
								/>
							)}
						</div>
					</fieldset>
				))}
			</div>

			<div className={clsx(styles.filterFooter)}>
				{hasFiltersSelected && (
					<div className={styles.deleteFiltersButton}>
						<Button
							variant="tertiary-dark"
							title={intl.formatMessage(
								{ id: 'filterButton.deleteFilters' },
								{
									amount: amountOfActiveFilters
								}
							)}
							onClick={handleDeleteFilters}
						/>
					</div>
				)}
				<Button
					title={intl.formatMessage({ id: 'global.showResults' })}
					onClick={() => setMobileIsOpen(false)}
					fullWidth
				/>
			</div>
		</ReactFocusLock>
	);
};

export default FilterSidebar;
